/**
 * §5 Permission Matrix — Nymbl Staff System Design v3.0
 *
 * This module is the single source of truth for role × capability access.
 * Every route guard must consult this matrix rather than hand-coding role checks.
 *
 * Encoding rules (from §5 spec table):
 *   "✓"         → { kind: "any" }     — unrestricted access for the role
 *   "own"        → { kind: "ownSolution" } or { kind: "self" } per row semantics
 *   "own Accounts" → { kind: "ownAccount" }
 *   "self only"  → { kind: "self" }
 *   blank / "—"  → capability omitted from the role's record (denied)
 *   "suggest only" → omitted (SYSTEM cannot write, only suggest; enforced at route layer)
 *   "confirm"    → { kind: "any" } for RM (RM writes rm_confirmed_rating)
 *   "self submit" → { kind: "self" } for SA (SA writes self_rating on own resource)
 */

import type { NymblRole } from "@/lib/auth";
import { isNymblRole } from "@/lib/nymbl-roles";

// ── Capability registry ──────────────────────────────────────────────────────

export const CAPABILITIES = [
  /**
   * Solutions Dashboard — view the triage hub.
   * ADMIN/RM: any. SA: own Solutions. ACCT: own Accounts. FIN: any.
   */
  "viewSolutionsDashboard",

  /**
   * Solution Detail — view a single solution's full operational profile.
   * ADMIN/RM: any. SA: own Solutions. ACCT: own Accounts. FIN: any.
   */
  "viewSolutionDetail",

  /**
   * Configure Pod Size on Solution — set composition (Solo / Pair / Trio).
   * RM only. SYSTEM: suggest only (omitted; not a write capability for SYSTEM).
   */
  "configurePodSize",

  /**
   * Override Pod Size from tier default — override with an explicit reason.
   * RM only.
   */
  "overridePodSizeFromTierDefault",

  /**
   * Assign / change R1, R2, R3 — set or swap Primary assignments.
   * RM only. SYSTEM: suggest only (omitted).
   */
  "assignPrimary",

  /**
   * Assign / change Supporting — add or remove Supporting assignments.
   * RM only. SYSTEM: suggest only (omitted).
   */
  "assignSupporting",

  /**
   * View Resource Directory — browse the bench.
   * ADMIN/RM: any. SA: self row only. FIN: any. ACCT: denied.
   */
  "viewResourceDirectory",

  /**
   * View Resource Detail — full profile of one Resource.
   * ADMIN/RM: any. SA: self only. FIN: any. ACCT: denied.
   */
  "viewResourceDetail",

  /**
   * Edit Resource constraint ratings.
   * RM/ADMIN: confirm path (any scope on rm_confirmed_rating).
   * SA: self-submit path (self scope on self_rating).
   * Route layer enforces which column each role writes.
   */
  "editResourceConstraintRating",

  /**
   * Historically: edit Resource competency grades (proficiency 1–5). The Competency UI and
   * its PUT route are gone (docs/decisions/0003, addendum 2026-10-02); the tables stay
   * read-only for audit. The capability survives ONLY because the Discipline grant /
   * revoke routes in src/index.ts still gate on it (TODO(perms): move them to their own
   * capability, then remove this one).
   * RM/ADMIN: any.
   */
  "editResourceCompetencyProficiency",

  /**
   * Assign / modify / confirm a Resource's Discipline Rating (1–5) — the
   * Expectation Match input (docs/specs/discipline-rating-fit-score.md).
   * RM/ADMIN: any. Unlike editResourceConstraintRating, there is NO self-submit
   * path for SA — D-0715-E removed SA self-submission from this flow, so SA
   * is intentionally absent from this capability's scope entirely.
   */
  "editResourceDisciplineRating",

  /**
   * Edit Resource capacity (point budget).
   * RM/ADMIN: any.
   */
  "editResourceCapacity",

  /**
   * Reserve a Resource for an Account, change the reservation, or release it — Resource
   * Detail ▸ Reservation (docs/specs/resource-reservation.md).
   * ADMIN/RM: any. SA/ACCT/FIN/SYSTEM: denied.
   *
   * A reservation is a portfolio call about which Account a Resource's open time is
   * reserved for. It touches no rating, capacity, Assignment, or Fit Score input, and every
   * write is audited (`resource_reservation_changed`).
   */
  "manageResourceReservation",

  /**
   * View Assignments Ops page — cross-book assignment view.
   * ADMIN/RM: any. ACCT: own Accounts. FIN: any. SA: denied.
   */
  "viewAssignmentsOps",

  /**
   * View Schedule Heatmap.
   * ADMIN/RM: any. SA: self row only. ACCT: own Account rows. FIN: any.
   */
  "viewScheduleHeatmap",

  /**
   * View Fit Calculator — transparency breakdown per Solution.
   * ADMIN/RM: any. SA: own Solutions. ACCT: own Accounts. FIN: denied.
   */
  "viewFitCalculator",

  /**
   * Edit Fit Score weights — per-Solution weight override.
   * RM only.
   */
  "editFitScoreWeights",

  /**
   * Manage Accounts — CRUD on client accounts (profile: name, industry,
   * externalNymblAccountId). Also covers reading the Account list and detail.
   * Unrestricted for every role (product decision: the Accounts section is not
   * gated by role) — every role holds { kind: "any" }, including ACCT, which
   * previously held only its own Accounts.
   */
  "manageAccounts",

  /**
   * Set Account Expectation Level.
   * RM-only per the Account Management spec (scoring input, not an ACCT concern).
   * ACCT does NOT hold this capability.
   */
  "setAccountExpectationLevel",

  /**
   * Set Account Engagement Standard Tier — and the Account's Default Rate Card, the same
   * pricing lever. ADMIN/RM: any (ADMIN added 2026-10-19). ACCT: own Accounts (direct
   * edit — no gate, per §9.5 Account Mgmt resolution).
   */
  "setAccountEngagementStandardTier",

  /**
   * Edit Account constraint profile — add / remove constraints on an Account.
   * RM: any. ACCT: own Accounts (direct edit — no gate, per Account Mgmt spec locked decision #1).
   * Editing constraints does NOT re-cascade to existing Solutions.
   */
  "editAccountConstraints",

  /**
   * Edit Solution Constraints — add / remove the Constraints a Solution requires (the
   * list its Fit Score's Constraint Match is computed against).
   * ADMIN/RM: any — ADMIN per product decision 2026-10-03 (§5 footnote ³). SA/ACCT/FIN:
   * denied. The two /api/dashboard/solutions/:id/constraints routes borrowed
   * configurePodSize as an RM-only proxy until then; Pod size and assignments stay RM-only.
   */
  "editSolutionConstraints",

  /**
   * Manage Configuration — constraints, intensity levels, tier defaults.
   * RM only.
   */
  "manageConfiguration",

  /**
   * Run Hours sync — trigger nightly Nymbl hours pull.
   * RM: any. FIN: any. SYSTEM: scheduled.
   */
  "runHoursSync",

  /**
   * Run import from Nymbl — persons / accounts / solutions, and the Pipeline Report Deal sync.
   * ADMIN: any (decision 2026-10-14). RM: any. SYSTEM: scheduled.
   */
  "runImportFromNymbl",

  /**
   * View Reporting & Analytics.
   * ADMIN/RM: any. ACCT: own Accounts. FIN: any. SA: denied.
   */
  "viewReportingAnalytics",

  /**
   * View Audit Trail.
   * ADMIN/RM: any. FIN: any. SA/ACCT: denied.
   */
  "viewAuditTrail",

  /**
   * View Staffing Requests queue + detail (docs/specs/staffing-requests.md).
   * ADMIN/RM/FIN: any. ACCT: own Accounts. SA: own Solutions.
   */
  "viewStaffingRequests",

  /**
   * Ask for a Pod change on a Solution (Add / Replace / Remove / Swap a Resource) — the
   * §5 "Request Change" workflow that routes to RM. Nothing changes until RM approves.
   * ADMIN/RM: any (2026-10-12 — so Pod changes are tracked as requests). ACCT: own
   * Accounts. SA: own Solutions. FIN: denied.
   */
  "requestStaffingChange",

  /**
   * Approve (applying the Pod change) or reject a Staffing Request. RM only — the same
   * role that holds assignPrimary / assignSupporting / configurePodSize.
   */
  "decideStaffingRequest",

  /**
   * Delete a Solution — hard, force-cascade removal for data cleanup.
   * RM only. SYSTEM excluded (manual action). Destructive + audited
   * (`solution_deleted`). See docs/specs/delete-solution.md.
   */
  "deleteSolution",

  /**
   * View Signals — access the signals triage hub (suggested Intensity, Pod changes).
   * ADMIN/RM: any. SA/ACCT/FIN: denied.
   */
  "viewSignals",

  /**
   * Process Solution On Demand — trigger on-demand Fit recalc for a Solution
   * (from pending Signals).
   * RM only.
   */
  "processSolutionOnDemand",

  /**
   * Confirm Suggested Intensity — mark a suggested Intensity signal as confirmed,
   * locking it to the Solution.
   * RM only.
   */
  "confirmSuggestedIntensity",

  /**
   * Apply Suggested Pod Change — accept a suggested Pod composition or assignment
   * change and commit it.
   * RM only.
   */
  "applySuggestedPodChange",

  /**
   * Apply Add Resource — apply a resource_off_pod Signal by adding the suggested
   * off-Pod Resource to the Pod (a Primary Position or Supporting) via the normal
   * audited staffing path.
   * RM only.
   */
  "applyAddResource",

  /**
   * Dismiss Signal — dismiss a Signal as non-actionable or false-positive.
   * RM only.
   */
  "dismissSignal",

  /**
   * View System Logs — read the technical observability log (errors + API
   * interactions) on the System → Logs page. Read-only surface.
   * ADMIN/RM: any. SA/ACCT/FIN: denied (logs expose paths, actor ids, and stack
   * traces across the whole book). Distinct from viewAuditTrail (business audit).
   * See docs/specs/system-logging.md.
   */
  "viewSystemLogs",

  /**
   * View the Availability report — read-only hours (Utilization) per Resource per month:
   * Booked vs Available Hours from NOS Guidance, time off and actuals. Hours only, no
   * cost, rates or points. Every role: any (product decision 2026-10-08, every page is
   * viewable by every role). SYSTEM: denied (no UI).
   */
  "viewAvailabilityReport",

  /**
   * Maintain Rate Cards and Planning Constants — Resource Plan Epics 12 and 14
   * (docs/specs/resource-plan.md §12). Covers bill rates per (Labor Category × Rate
   * Basis), the Tier → Rate Basis map, per-basis margin floor overrides, and the
   * planning constants in `system_config`.
   * ADMIN/RM/FIN: any. SA/ACCT: denied.
   *
   * Distinct from `manageConfiguration` (ADMIN/RM) on purpose: the Rate Card is [FIN]'s
   * artefact and FIN holds no `manageConfiguration`. Labor Category maintenance
   * (Epic 11) rides on `manageConfiguration`, per §12.
   */
  "manageRateCard",

  /**
   * View cost rates, cost dollars, and Staffed Margin — Resource Plan §12.
   * ADMIN/RM/FIN: any. SA/ACCT: DENIED.
   *
   * Non-negotiable 3: cost never leaves the server for [ACCT]. This capability drives
   * response SHAPING at the API boundary, not a UI toggle — an [ACCT] payload must not
   * contain cost keys at all. See shapeCardForActor() in src/api/rate-cards.ts and the
   * assertion in its test. Margin % and floor status carry no capability of their own;
   * they ride on the read and are safe for [ACCT] because they are percentages.
   */
  "viewPlanCost",

  /**
   * See a Resource's own Internal Rate (Resource-level cost) — product decision 2026-10-14,
   * docs/specs/resource-internal-rates.md. ADMIN/FIN/RM: any. SA/ACCT: DENIED.
   *
   * RM was denied on 2026-10-14 and granted on 2026-10-15 (Martyn): Resource Managers see
   * each named Resource's cost and margin on the plan grid. Drives two things: the
   * Configuration → Internal Rates page (read-only for RM, and still behind a step-up
   * re-authentication), and response SHAPING of Resource-priced plan lines — without it,
   * such a line's cost rate, cost, net and margin % are null.
   */
  "viewResourceInternalRates",

  /**
   * Add or remove a Resource's Internal Rate — product decision 2026-10-14.
   * ADMIN/FIN: any. RM/SA/ACCT: DENIED. Every write also requires a step-up.
   */
  "manageResourceInternalRates",

  /**
   * Create / edit a Resource Plan, including its Planned Pod and status transitions —
   * Resource Plan §12, ratified RP-D1(a) 2026-09-07. ADMIN added by product decision
   * 2026-09-17: ADMIN authors on any Account, same as RM. SA/FIN: denied.
   * RM/ADMIN: any. ACCT: own Accounts.
   *
   * Extends the established `ownAccount` direct-edit pattern ACCT already holds via
   * `manageAccounts` / `setAccountEngagementStandardTier` / `editAccountConstraints`
   * (resolved 2026-06-29). Enforced from M2, when plans exist.
   */
  "manageResourcePlan",

  /**
   * Create a `Pending` Solution from the plan form — Resource Plan §12, RP-D1(b).
   * RM: any. ACCT: own Accounts. ADMIN/SA/FIN: denied.
   *
   * Deliberately narrower than a Solution write: it permits creating a Solution in
   * `status = Pending` only, never editing a live one. ACCT holds no other Solution
   * write capability and that stays true.
   */
  "createPendingSolution",

  /**
   * Note a Suggested Resource on a Plan Line via a restricted picker — Resource Plan
   * RP-18.2, ratified RP-D1(c) 2026-09-07.
   * ADMIN/RM: any. ACCT: own Accounts. SA/FIN: denied.
   *
   * A NEW, NARROWER capability — not a relaxation of `viewResourceDirectory`, which
   * stays denied to ACCT. The picker returns name, Discipline, and prior Account history
   * only: no capacity, no ratings, no cost, and no link through to Resource Detail.
   * Widening the existing directory grant instead would leak the whole bench.
   */
  "viewResourcePickerForPlan",

  /**
   * Approve or reject a Resource Plan below the margin floor, or carrying a below-cost
   * line — Resource Plan §12, Epic 17.
   * FIN/ADMIN: any. RM/SA/ACCT: denied.
   *
   * §12 originally granted this to `[FIN]` alone. ADMIN added by product decision
   * 2026-10-06, with no self-approval guard: ADMIN also holds `manageResourcePlan`, so an
   * ADMIN can approve a concession on a plan it authored. That is accepted, not
   * overlooked — the decision is still attributed and audited (`plan_approval_decided`).
   *
   * RP-17.2's "the approver set comes from Configuration" is a refinement WITHIN this grant
   * — naming individual approvers — and is deliberately not built: a named list stored in
   * `system_config` would be a second, weaker authority sitting beside the permission
   * matrix, and the matrix is the enforcement point (§12). When per-user approvers are
   * wanted, they belong in a table with an FK to `user`, not in a JSON blob.
   *
   * It stays DENIED to RM, SA and ACCT, who author plans. RP-17.1 exists so their
   * concessions are "decided, not discovered". RM still SEES the request in full — that
   * rides on `viewPlanCost`, because the shortfall is a cost-derived figure.
   */
  "approveResourcePlan",

  /**
   * Reject a Resource Plan that is In Review or Staffing back to its author, with a required
   * reason — product decision 2026-10-07; eligibility moved from "Proposed or Won" to the
   * Resourcing status on 2026-10-28. RM/ADMIN: any. SA/ACCT/FIN: denied.
   *
   * Distinct from `approveResourcePlan`, which decides the margin-floor gate and is FIN /
   * ADMIN. This is the Resource Manager's own "this cannot go ahead as written": the plan's
   * Resourcing status becomes Returned (its Draft / Proposed / Won status is untouched) and
   * the author reads the reason on the plan. ADMIN holds it alongside RM, as for
   * `setPlanResourcingStatus`.
   *
   * Since RP-34 a rejection IS a Blocking Staffing Concern, so this also gates marking a
   * Concern Blocking (src/api/plan-concerns.ts).
   */
  "rejectResourcePlan",

  /**
   * Lock a Resource Plan, and unlock it — product decision 2026-10-14. RM/ADMIN: any.
   * SA/ACCT/FIN: denied. A Locked plan refuses every edit until it is unlocked, so the
   * lock protects a plan from the people who can otherwise edit it, Account owners
   * included. Enforced in src/api/plan-lifecycle.ts (lockPlan / unlockPlan).
   *
   * Replaces `staffPlan` (RP-18.5's RM staffing edits), which no path checked once every
   * plan became editable in every status; it carried the same grants.
   */
  "lockResourcePlan",

  /**
   * RP-32.2 / 32.3 — add, remove and promote a Plan Line's Candidates (the RM's shortlist,
   * up to three per line). ADMIN/RM: any. SA/ACCT/FIN: denied — product decision
   * 2026-10-15 (Martyn): "RM and Admin only". ACCT and SA still set the Suggested Resource
   * itself under `manageResourcePlan`. Reading Candidates rides on `viewResourcePlan`.
   * A Candidate books nothing: no capacity, no Fit Score input, no margin effect.
   * Enforced in src/api/plan-line-candidates.ts.
   */
  "managePlanCandidates",

  /**
   * View a Resource Plan — Resource Plan §12.
   * ADMIN/RM/FIN: any. ACCT: own Accounts. SA: denied.
   *
   * Margin % and floor status carry no capability of their own — they ride on this read,
   * and are safe for [ACCT] because they are percentages. Cost fields are stripped
   * server-side for anyone lacking `viewPlanCost` (non-negotiable 3), so this grant is
   * about WHICH PLANS you can see, never which fields.
   *
   * Distinct from `manageResourcePlan`: FIN and ADMIN read every plan but author none.
   */
  "viewResourcePlan",

  /**
   * Set a Resource Plan's Resourcing status (InReview / Staffing / Staffed) — the RM's
   * own staffing flag, independent of the plan's sales status.
   * ADMIN/RM: any. SA/ACCT/FIN: denied.
   *
   * Setting **Staffed** is a commit point: it is refused until every Plan Line is
   * Confirmed, and it writes the confirmed Resources onto the Solution as Assignments
   * (Primary for a line positioned R1–R4, Supporting otherwise), with points sized from
   * their planned hours. ADMIN holds this by product decision 2026-10-06 — scoped to
   * this one action; ADMIN still holds neither `assignPrimary` nor `assignSupporting`.
   */
  "setPlanResourcingStatus",

  /**
   * Split a Plan Line across Resources — Line Shares (RP-24, approved 2026-10-17).
   * ADMIN/RM: any. SA/ACCT/FIN: denied.
   *
   * Writes the Shares under a line (Resource, hours per month, RM-entered points). A Share
   * has no capacity effect on its own: it takes effect only when the plan's Resourcing
   * status is set to Staffed (`setPlanResourcingStatus`), so the grant matches that one.
   * Also gates READING a Share's points: every other reader of the plan sees the Share
   * holders' names and hours, never points (RP-24.4).
   */
  "managePlanLineShares",

  /**
   * Set a Resource Plan's Chance of Close — Staff's own figure in place of the NOS Deal
   * Stage %, with a required comment naming who gave it (spec Epic 36, product decision
   * 2026-10-17) — or put it back to following NOS. Every change is kept.
   * ADMIN/RM: any. ACCT: own Accounts ("CP" = the Account Manager). SA/FIN: denied —
   * they read it with the plan.
   *
   * Deliberately NOT `manageResourcePlan`, which ACCT and SA hold on every Account: the
   * figure is a staffing judgement, and Martyn scoped it to the RM and the Account's own
   * Account Manager.
   */
  "setChanceOfClose",

  /**
   * Raise a Staffing Concern on a Resource Plan or one of its Plan Lines (RP-34, approved
   * 2026-10-17). RM/ADMIN: any. SA/ACCT/FIN: denied.
   *
   * The Resource Manager's "I cannot staff this as planned". Marking the Concern Blocking
   * additionally needs `rejectResourcePlan` (same holders): a Blocking Concern returns the
   * plan to its author exactly as Reject does. Resolving one is `resolvePlanConcern`.
   */
  "raisePlanConcern",

  /**
   * Acknowledge a Blocking Staffing Concern — the account side's "we have seen it", which
   * lets the plan be marked Staffed again (RP-34.3). ADMIN: any. ACCT: own Accounts.
   * RM/SA/FIN: denied.
   *
   * The plan's Author (owner) may also acknowledge whatever their role: that rule is
   * per-plan data, so src/api/plan-concerns.ts applies it beside this grant. Whoever raised
   * a Concern can never acknowledge it, so the RM cannot clear their own blocker.
   */
  "acknowledgePlanConcern",

  /**
   * Resolve a Staffing Concern, with an optional note (RP-34). RM/ADMIN: any.
   * SA/ACCT/FIN: denied. Resolving the Blocking Concern that returned a plan moves its
   * Resourcing status back to In Review.
   */
  "resolvePlanConcern",

  /**
   * Administer workspace users — the Settings → Users surface. Covers reading the user
   * list and a user's detail, provisioning and removing users, setting a role by hand,
   * handing a role back to automatic mapping, enabling / disabling access, revoking open
   * sessions, resetting a password, maintaining the automatic role-mapping rules, AND
   * granting or revoking Client Account ownership.
   * ADMIN/RM: any. SA/ACCT/FIN/SYSTEM: denied.
   *
   * Account ownership lives HERE and not on `manageAccounts` on purpose. Granting an
   * Account to a user is not an edit to the Account — it decides who the §5 `ownAccount`
   * scope resolves for, i.e. it is a permission write. `manageAccounts` is `{ kind: "any" }`
   * for every role (the Accounts section is not gated), so hanging ownership off it would
   * let an [ACCT] user grant themselves the rest of the book.
   *
   * Every write under this capability is audited (`user_*` / `account_owner_*` event
   * types). That is why the routes are ours rather than Better Auth's admin endpoints
   * called straight from the browser: the plugin writes the row and leaves no trail.
   * The handlers still call `auth.api.*` with the caller's headers, so the plugin's own
   * `adminRoles: ["ADMIN","RM"]` check runs inside this one.
   */
  "manageUsers",

  /**
   * Edit RM Decision Log entries (notes/decisions/next actions) — the
   * Reporting page's RM Decision Log tab.
   * RM only. Reading the tab rides on `viewReportingAnalytics` (ADMIN/RM/FIN
   * any, ACCT own Accounts); this capability gates the three write fields.
   */
  "manageDecisionLog",

  /**
   * Configure Resource Rundown settings — band thresholds, Standard Monthly
   * Hours, Win detection threshold, and per-Discipline Billable Target defaults.
   * RM (admin) only; ADMIN, FIN, ACCT, SA denied (Resource Rundown spec §5
   * "Configure Rundown settings").
   *
   * This gates CONFIGURATION, not a Billable Target itself. A Resource's target
   * changes only through an approved Target Adjustment (RR-G / AC9): there is no
   * inline per-Resource edit behind this capability or any other.
   */
  "configureRundownSettings",

  /**
   * View the Resources page — the org-wide Resource directory screen, from the sidebar
   * or by URL. PAGE-level only: the Resource data other screens need (Pod member names,
   * the plan Resource picker, Supporting load, the Resource Detail drawer) stays on
   * `viewResourceDirectory` / `viewResourceDetail`, which every role still holds.
   * ADMIN/RM/ACCT/FIN: any. SA: denied (product decision 2026-10-20).
   */
  "viewResourcesPage",

  /**
   * See System pages and Settings tabs whose changes this role cannot make — they render
   * view-only. Without it a role sees only the System items it can act on, plus those
   * with nothing to change (Logs, Settings → Signals).
   * ADMIN/RM/ACCT/FIN: any. SA: denied (product decision 2026-10-20).
   */
  "viewSystemPagesReadOnly",

  /**
   * Report an Issue — the life ring's Report an Issue form, and reading, commenting on and
   * closing one's OWN Issues (the API scopes those to the reporter).
   * Every signed-in role: any.
   */
  "submitIssue",

  /**
   * Work the Issue queue — read every reported Issue with its captured console and network
   * trails and the un-marked screenshot, set status / priority / owner, comment on any,
   * retry the Delyver push. ADMIN only (product decision 2026-10-19).
   */
  "workIssueQueue",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

// ── Scope types ──────────────────────────────────────────────────────────────

export type Scope =
  /** Unrestricted access — role can read/write any record of this type. */
  | { kind: "any" }
  /** SA accessing their own Resource row only. */
  | { kind: "self" }
  /** ACCT accessing Accounts/Solutions they own. */
  | { kind: "ownAccount" }
  /** SA accessing Solutions where their linked Resource is a Primary (R1/R2/R3). */
  | { kind: "ownSolution" }
  /** Explicitly denied (useful as a marker; absence from the record also means denied). */
  | { kind: "denied" };

// ── Denial reasons ────────────────────────────────────────────────────────────
//
// Plain-language reason appended to a role-level 403 so the person who clicked
// sees why, not just the capability name. Optional per capability.

export const CAPABILITY_DENIED_REASONS: Partial<Record<Capability, string>> = {
  runImportFromNymbl: "only Admins and Resource Managers can import from Nymbl",
};

// ── Permission Matrix ─────────────────────────────────────────────────────────
//
// Keyed by role. Capabilities absent from a role's record are denied.
// Mirrors §5 row-by-row. Column order in comments matches the spec table.

export const PERMISSION_MATRIX: Record<NymblRole | "SYSTEM", Partial<Record<Capability, Scope>>> = {
  //
  // ── ADMIN ──────────────────────────────────────────────────────────────────
  // Workspace operator (formerly CEO, renamed 2026-10-02). Portfolio visibility
  // across the book; writes workspace Configuration, Rate Cards, users, Accounts,
  // Resource Plans, Resource attributes, Resource reservations and Solution Constraints. No
  // direct staffing writes — Pod composition and assignments stay RM's. The one exception
  // is setPlanResourcingStatus: setting a plan to Staffed commits its confirmed Resources
  // (product decision 2026-10-06).
  //
  ADMIN: {
    viewSolutionsDashboard: { kind: "any" },
    viewSolutionDetail: { kind: "any" },
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    viewResourceDirectory: { kind: "any" },
    viewResourceDetail: { kind: "any" },
    // Solution Constraints are Solution configuration, not a staffing write: ADMIN edits
    // them on the same terms as RM (product decision 2026-10-03, §5 footnote ³).
    editSolutionConstraints: { kind: "any" },
    // ADMIN edits Resource attributes — details/capacity, constraint ratings,
    // competency grades + Discipline grants, Discipline Ratings — on the same terms
    // as RM, per product decision 2026-10-02. An ADMIN constraint-rating write IS the
    // confirmation (§2.1 #5: "RM or ADMIN confirmation"); every write stays
    // attributed in the audit trail.
    editResourceConstraintRating: { kind: "any" },
    editResourceCompetencyProficiency: { kind: "any" },
    editResourceDisciplineRating: { kind: "any" },
    editResourceCapacity: { kind: "any" },
    // Reserve a Resource for an Account — RM and ADMIN, product decision 2026-10-02.
    manageResourceReservation: { kind: "any" },
    viewAssignmentsOps: { kind: "any" },
    viewScheduleHeatmap: { kind: "any" },
    viewFitCalculator: { kind: "any" },
    // editFitScoreWeights: denied
    // manageAccounts: not gated by role — every role holds { kind: "any" }.
    manageAccounts: { kind: "any" },
    // setAccountExpectationLevel: denied
    // ADMIN sets an Account's Engagement Standard Tier and Default Rate Card on the same
    // terms as RM — "allow an admin to set the tier and the default rate card", product
    // decision 2026-10-19 (specs/workspaces/feat-set-tier-and-default-rate-card-t9re.md).
    setAccountEngagementStandardTier: { kind: "any" },
    // ADMIN manages workspace configuration (Settings → config tabs + Integrations
    // list/refresh). Granted per product decision 2026-06-29 amending §5's
    // original read-only profile.
    manageConfiguration: { kind: "any" },
    // runHoursSync: denied
    // ADMIN imports Solutions/Accounts and syncs the Pipeline Report alongside RM —
    // product decision 2026-10-14 (Admins could refresh Integrations but every
    // Import click was refused).
    runImportFromNymbl: { kind: "any" },
    viewReportingAnalytics: { kind: "any" },
    viewAuditTrail: { kind: "any" },
    // ADMIN reads the Staffing Requests queue and may RAISE a request (product decision
    // 2026-10-12: Pod changes are tracked as requests). It still cannot decide one.
    viewStaffingRequests: { kind: "any" },
    requestStaffingChange: { kind: "any" },
    // decideStaffingRequest: denied
    // ADMIN views the Signals triage hub.
    viewSignals: { kind: "any" },
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
    // ADMIN reads the System → Logs observability surface.
    viewSystemLogs: { kind: "any" },
    viewAvailabilityReport: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role)
    // ── Resource Plan (docs/specs/resource-plan.md §12) ──
    // ADMIN maintains Rate Cards and Planning Constants alongside manageConfiguration.
    manageRateCard: { kind: "any" },
    viewPlanCost: { kind: "any" },
    // Resource-level cost (2026-10-14). RM, SA and ACCT are denied.
    viewResourceInternalRates: { kind: "any" },
    manageResourceInternalRates: { kind: "any" },
    // manageResourcePlan: any — product decision 2026-09-17, ADMIN authors on any Account.
    manageResourcePlan: { kind: "any" },
    // createPendingSolution: denied
    // ADMIN approves plans alongside FIN — product decision 2026-10-06, including plans
    // ADMIN authored itself (see the capability's doc comment).
    approveResourcePlan: { kind: "any" },
    rejectResourcePlan: { kind: "any" },
    lockResourcePlan: { kind: "any" },
    managePlanLineShares: { kind: "any" },
    // Staffing Concerns (RP-34): ADMIN raises and resolves alongside RM, and can
    // acknowledge a Blocking one when the account side cannot.
    raisePlanConcern: { kind: "any" },
    acknowledgePlanConcern: { kind: "any" },
    resolvePlanConcern: { kind: "any" },
    managePlanCandidates: { kind: "any" }, // RP-32 — RM/ADMIN only (2026-10-15)
    // ADMIN can note a Suggested Resource via the restricted picker.
    viewResourcePickerForPlan: { kind: "any" },
    viewResourcePlan: { kind: "any" },
    // Product decision 2026-10-06: ADMIN sets a plan's Resourcing status, including the
    // Staffed commit that writes the plan's confirmed Resources onto the Solution.
    setPlanResourcingStatus: { kind: "any" },
    setChanceOfClose: { kind: "any" },
    // manageDecisionLog: denied — RM only.
    // configureRundownSettings: denied — RM (admin) only, per Rundown spec §5.
    // ADMIN administers workspace users alongside manageConfiguration.
    manageUsers: { kind: "any" },
    // Page-level visibility (2026-10-20): every role but SA opens the Resources page and the
    // System pages it can only read.
    viewResourcesPage: { kind: "any" },
    viewSystemPagesReadOnly: { kind: "any" },
    // Reported Issues (2026-10-19): ADMIN reports and works the queue.
    submitIssue: { kind: "any" },
    workIssueQueue: { kind: "any" },
  },

  //
  // ── RM ─────────────────────────────────────────────────────────────────────
  // Owns staffing decisions. Full write across the book.
  //
  RM: {
    viewSolutionsDashboard: { kind: "any" },
    viewSolutionDetail: { kind: "any" },
    configurePodSize: { kind: "any" },
    overridePodSizeFromTierDefault: { kind: "any" },
    assignPrimary: { kind: "any" },
    assignSupporting: { kind: "any" },
    viewResourceDirectory: { kind: "any" },
    viewResourceDetail: { kind: "any" },
    // RM confirms ratings (writes rm_confirmed_rating). Scope "any" — RM can
    // confirm any Resource's ratings, not just their own.
    editResourceConstraintRating: { kind: "any" },
    editResourceCompetencyProficiency: { kind: "any" },
    // RM assigns/modifies/confirms Discipline Ratings for any Resource. No SA
    // self-submit scope exists for this capability (D-0715-E).
    editResourceDisciplineRating: { kind: "any" },
    editResourceCapacity: { kind: "any" },
    manageResourceReservation: { kind: "any" },
    viewAssignmentsOps: { kind: "any" },
    viewScheduleHeatmap: { kind: "any" },
    viewFitCalculator: { kind: "any" },
    editFitScoreWeights: { kind: "any" },
    manageAccounts: { kind: "any" },
    setAccountExpectationLevel: { kind: "any" },
    setAccountEngagementStandardTier: { kind: "any" },
    editAccountConstraints: { kind: "any" },
    editSolutionConstraints: { kind: "any" },
    manageConfiguration: { kind: "any" },
    runHoursSync: { kind: "any" },
    runImportFromNymbl: { kind: "any" },
    viewReportingAnalytics: { kind: "any" },
    viewAuditTrail: { kind: "any" },
    // RM decides Staffing Requests, and may raise one so a Pod change it makes is tracked
    // as a request (product decision 2026-10-12) — it can still edit the Pod directly.
    viewStaffingRequests: { kind: "any" },
    requestStaffingChange: { kind: "any" },
    decideStaffingRequest: { kind: "any" },
    deleteSolution: { kind: "any" },
    // RM manages the Signals triage hub: view, process, confirm, apply, dismiss.
    viewSignals: { kind: "any" },
    processSolutionOnDemand: { kind: "any" },
    confirmSuggestedIntensity: { kind: "any" },
    applySuggestedPodChange: { kind: "any" },
    applyAddResource: { kind: "any" },
    dismissSignal: { kind: "any" },
    // RM reads the System → Logs observability surface.
    viewSystemLogs: { kind: "any" },
    viewAvailabilityReport: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role)
    // ── Resource Plan (docs/specs/resource-plan.md §12) ──
    manageRateCard: { kind: "any" },
    viewPlanCost: { kind: "any" },
    // Granted 2026-10-15 (was denied 2026-10-14): RM sees a named Resource's cost and
    // margin. Adding or removing a rate stays ADMIN/FIN (manageResourceInternalRates).
    viewResourceInternalRates: { kind: "any" },
    manageResourcePlan: { kind: "any" },
    // approveResourcePlan: denied — an approval its author can grant itself is not a
    // control. Plan approval is FIN and ADMIN only; RM still sees the request in full
    // via viewPlanCost.
    rejectResourcePlan: { kind: "any" },
    lockResourcePlan: { kind: "any" },
    managePlanLineShares: { kind: "any" },
    // Staffing Concerns (RP-34). acknowledgePlanConcern: denied — the RM raises the
    // blocker, so clearing it is the account side's call, never the RM's own.
    raisePlanConcern: { kind: "any" },
    resolvePlanConcern: { kind: "any" },
    managePlanCandidates: { kind: "any" }, // RP-32 — RM/ADMIN only (2026-10-15)
    createPendingSolution: { kind: "any" },
    viewResourcePickerForPlan: { kind: "any" },
    viewResourcePlan: { kind: "any" },
    setPlanResourcingStatus: { kind: "any" },
    setChanceOfClose: { kind: "any" },
    // RM owns the Decision Log end-to-end.
    manageDecisionLog: { kind: "any" },
    // RM admin owns Rundown configuration (spec §5).
    configureRundownSettings: { kind: "any" },
    // RM administers workspace users: roles, access, sessions, and Account ownership.
    manageUsers: { kind: "any" },
    // Page-level visibility (2026-10-20): every role but SA opens the Resources page and the
    // System pages it can only read.
    viewResourcesPage: { kind: "any" },
    viewSystemPagesReadOnly: { kind: "any" },
    // Reported Issues (2026-10-19): every role reports; only ADMIN works the queue.
    submitIssue: { kind: "any" },
  },

  //
  // ── SA ─────────────────────────────────────────────────────────────────────
  // Own profile, own assignments, own ratings (self-submit only).
  //
  SA: {
    // SA sees own Solutions only (Solutions where their Resource is a Primary).
    viewSolutionsDashboard: { kind: "ownSolution" },
    viewSolutionDetail: { kind: "ownSolution" },
    // editSolutionConstraints: denied
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    // Resource DATA stays open to SA (2026-10-08): Pod member names, the plan picker's
    // Fit Scores and the Supporting load all read it. The Resources PAGE does not — see
    // viewResourcesPage, denied below.
    viewResourceDirectory: { kind: "any" },
    viewResourceDetail: { kind: "any" },
    // SA self-submits constraint ratings (writes self_rating on own Resource).
    editResourceConstraintRating: { kind: "self" },
    // editResourceCompetencyProficiency: denied
    // editResourceDisciplineRating: denied
    // editResourceCapacity: denied
    // viewAssignmentsOps: denied
    // SA sees only their own row in the heatmap.
    viewScheduleHeatmap: { kind: "self" },
    // SA sees Fit Calculator for own Solutions only.
    viewFitCalculator: { kind: "ownSolution" },
    // SA asks for Pod changes on Solutions it sits on, and tracks them; RM decides.
    viewStaffingRequests: { kind: "ownSolution" },
    requestStaffingChange: { kind: "ownSolution" },
    // decideStaffingRequest: denied
    // editFitScoreWeights: denied
    // manageAccounts: not gated by role — every role holds { kind: "any" }.
    manageAccounts: { kind: "any" },
    // setAccountExpectationLevel: denied
    // setAccountEngagementStandardTier: denied
    // manageConfiguration: denied
    // runHoursSync: denied
    // runImportFromNymbl: denied
    viewReportingAnalytics: { kind: "ownAccount" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    // viewAuditTrail: denied
    viewSignals: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
    // ── Resource Plan (docs/specs/resource-plan.md §12) ──
    // manageRateCard: denied — the Rate Card stays [FIN]'s artefact.
    // viewPlanCost: DENIED. Cost rates/dollars never reach SA; same non-negotiable as ACCT.
    // Product decision 2026-09-30: open plan authoring/review up to SA — any Account, not
    // just their own Solutions — so review/edit is never blocked on ownership plumbing.
    manageResourcePlan: { kind: "any" },
    // approveResourcePlan: denied — plan approval is FIN and ADMIN only.
    createPendingSolution: { kind: "any" },
    viewResourcePickerForPlan: { kind: "any" },
    viewResourcePlan: { kind: "any" },
    // manageUsers: denied — user administration is ADMIN/RM only.
    // Logs has nothing to change, so SA keeps it (product decision 2026-10-20).
    viewSystemLogs: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    viewAvailabilityReport: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role)
    // viewResourcesPage: denied — SA does not open the Resources page (2026-10-20).
    // viewSystemPagesReadOnly: denied — SA sees only the System items it can act on, or
    // that have nothing to change (2026-10-20).
    // Reported Issues (2026-10-19): every role reports; only ADMIN works the queue.
    submitIssue: { kind: "any" },
  },

  //
  // ── ACCT ───────────────────────────────────────────────────────────────────
  // Account Manager. Per §9.5 Account Management resolution (2026-06-29):
  // ACCT can directly edit profile + constraints on their own Accounts.
  // Expectation Level is RM-only (scoring input, not an ACCT concern).
  //
  ACCT: {
    // ACCT sees Solutions under their own Accounts.
    viewSolutionsDashboard: { kind: "ownAccount" },
    viewSolutionDetail: { kind: "ownAccount" },
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    viewResourceDirectory: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    viewResourceDetail: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    // editResourceConstraintRating: denied
    // editResourceCompetencyProficiency: denied
    // editResourceDisciplineRating: denied
    // editResourceCapacity: denied
    // ACCT sees Assignments for their Accounts.
    viewAssignmentsOps: { kind: "ownAccount" },
    // ACCT sees heatmap rows for their Account's Solutions.
    viewScheduleHeatmap: { kind: "ownAccount" },
    // ACCT sees Fit Calculator for own Accounts' Solutions.
    viewFitCalculator: { kind: "ownAccount" },
    // editFitScoreWeights: denied
    // manageAccounts: not gated by role — every role holds { kind: "any" }.
    // (Previously { kind: "ownAccount" } per the §9.5 direct-edit resolution;
    // superseded by the decision to not gate the Accounts section by role at all.)
    manageAccounts: { kind: "any" },
    // setAccountExpectationLevel: denied — RM-only (scoring input).
    // Per §9.5 resolution: ACCT sets tier on own Accounts directly — no gate.
    setAccountEngagementStandardTier: { kind: "ownAccount" },
    // Per Account Mgmt spec locked decision #1: ACCT edits own Account constraints directly.
    editAccountConstraints: { kind: "ownAccount" },
    // editSolutionConstraints: denied — ACCT is read-only on Solutions (§5 open decision).
    // manageConfiguration: denied
    // runHoursSync: denied (spec: blank)
    // runImportFromNymbl: denied
    // ACCT sees Reporting for own Accounts.
    viewReportingAnalytics: { kind: "ownAccount" },
    // viewAuditTrail: denied
    // ACCT asks for Pod changes on its own Accounts' Solutions and tracks them —
    // §5's "Request Change" workflow that routes to RM. ACCT never edits the Pod.
    viewStaffingRequests: { kind: "ownAccount" },
    requestStaffingChange: { kind: "ownAccount" },
    // decideStaffingRequest: denied
    viewSignals: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
    // ── Resource Plan (docs/specs/resource-plan.md §12) ──
    // manageRateCard: denied — the Rate Card is [FIN]'s artefact, not ACCT's.
    // viewPlanCost: DENIED. Non-negotiable 3: cost never leaves the server for ACCT.
    //   Cost rates, cost dollars and list-vs-applied deltas are stripped at the API
    //   boundary, not hidden in the UI. ACCT receives margin % and floor status only.
    // The three ownAccount grants ratified RP-D1 (2026-09-07) were widened to "any" by
    // product decision 2026-09-30: ACCT authors and reviews plans across every Account,
    // not only the ones they own — the ownership link was blocking legitimate review/edit.
    manageResourcePlan: { kind: "any" },
    // Staffing Concerns (RP-34): ACCT acknowledges a Blocking Concern on its own
    // Accounts' plans; raising and resolving stay RM / ADMIN.
    acknowledgePlanConcern: { kind: "ownAccount" },
    // approveResourcePlan: denied — the whole point of RP-17.1 is that a concession the
    // author wants is decided by someone else.
    createPendingSolution: { kind: "any" },
    // RP-18.2 carve-out: name, Discipline and prior Account history only. This is a NEW,
    // narrower capability. (Pages were opened to every role on 2026-10-08, so ACCT can now
    // also read the directory; the picker is still its own, deliberately small grant.)
    viewResourcePickerForPlan: { kind: "any" },
    // Read is opened the same way authoring is: an [ACCT] user sees every plan, and cost
    // is stripped from every one of them.
    viewResourcePlan: { kind: "any" },
    // Spec Epic 36 (2026-10-17): the Account Manager sets Chance of Close on the plans of
    // the Accounts they own — narrower than manageResourcePlan above, on purpose.
    setChanceOfClose: { kind: "ownAccount" },
    // manageUsers: denied — and note this is what stops an [ACCT] user granting
    // themselves the Accounts their own ownAccount scope resolves against.
    viewSystemLogs: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    viewAvailabilityReport: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role)
    // Page-level visibility (2026-10-20): every role but SA opens the Resources page and the
    // System pages it can only read.
    viewResourcesPage: { kind: "any" },
    viewSystemPagesReadOnly: { kind: "any" },
    // Reported Issues (2026-10-19): every role reports; only ADMIN works the queue.
    submitIssue: { kind: "any" },
  },

  //
  // ── FIN ────────────────────────────────────────────────────────────────────
  // Finance / Ops. Utilization, hours, reconciliation. Read-only on staffing.
  //
  FIN: {
    viewSolutionsDashboard: { kind: "any" },
    viewSolutionDetail: { kind: "any" },
    // editSolutionConstraints: denied
    // configurePodSize: denied
    // overridePodSizeFromTierDefault: denied
    // assignPrimary: denied
    // assignSupporting: denied
    viewResourceDirectory: { kind: "any" },
    viewResourceDetail: { kind: "any" },
    // editResourceConstraintRating: denied
    // editResourceCompetencyProficiency: denied
    // editResourceDisciplineRating: denied
    // editResourceCapacity: denied
    viewAssignmentsOps: { kind: "any" },
    viewScheduleHeatmap: { kind: "any" },
    viewFitCalculator: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    // editFitScoreWeights: denied
    // manageAccounts: not gated by role — every role holds { kind: "any" }.
    manageAccounts: { kind: "any" },
    // setAccountExpectationLevel: denied
    // setAccountEngagementStandardTier: denied
    // manageConfiguration: denied
    // Spec grants FIN runHoursSync.
    runHoursSync: { kind: "any" },
    // runImportFromNymbl: denied
    viewReportingAnalytics: { kind: "any" },
    viewAuditTrail: { kind: "any" },
    // FIN reads the Staffing Requests queue; no write.
    viewStaffingRequests: { kind: "any" },
    // requestStaffingChange / decideStaffingRequest: denied
    viewSignals: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    // processSolutionOnDemand: denied
    // confirmSuggestedIntensity: denied
    // applySuggestedPodChange: denied
    // applyAddResource: denied
    // dismissSignal: denied
    // ── Resource Plan (docs/specs/resource-plan.md §12) ──
    // FIN owns the Rate Card and the Planning Constants, and sees cost everywhere.
    manageRateCard: { kind: "any" },
    viewPlanCost: { kind: "any" },
    // Resource-level cost (2026-10-14). RM, SA and ACCT are denied.
    viewResourceInternalRates: { kind: "any" },
    manageResourceInternalRates: { kind: "any" },
    // manageResourcePlan: denied — FIN approves plans, it does not author them.
    // FIN approves plans (Epic 17), alongside ADMIN. The one Resource Plan WRITE it holds.
    approveResourcePlan: { kind: "any" },
    // createPendingSolution: denied
    // viewResourcePickerForPlan: denied — FIN has no staffing-suggestion role.
    // FIN reads every plan (to approve it in M4) but authors none.
    viewResourcePlan: { kind: "any" },
    // manageUsers: denied — user administration is ADMIN/RM only.
    viewSystemLogs: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role; writes unchanged)
    viewAvailabilityReport: { kind: "any" }, // open to view 2026-10-08 (every page is viewable by every role)
    // Page-level visibility (2026-10-20): every role but SA opens the Resources page and the
    // System pages it can only read.
    viewResourcesPage: { kind: "any" },
    viewSystemPagesReadOnly: { kind: "any" },
    // Reported Issues (2026-10-19): every role reports; only ADMIN works the queue.
    submitIssue: { kind: "any" },
  },

  //
  // ── SYSTEM ─────────────────────────────────────────────────────────────────
  // Cron jobs, schedulers, recalc triggers. Bypasses scope but only on
  // capabilities the spec matrix marks as "scheduled".
  // "suggest only" capabilities are intentionally omitted — suggestions are not
  // writes; they are handled at the application layer, not the permission layer.
  //
  SYSTEM: {
    runHoursSync: { kind: "any" },
    runImportFromNymbl: { kind: "any" },
    // manageUsers: denied — a scheduler never changes who can sign in.
    // editSolutionConstraints: denied — sync writes to solution_constraint run under the
    // import (runImportFromNymbl) and refresh (manageConfiguration) routes, not this one.
  },
};

// ── Client-side capability check ─────────────────────────────────────────────

/**
 * Is a matrix cell a grant? An absent cell and the explicit `denied` marker are both
 * refusals — requireCapability (permissions-server.ts) treats them the same way, and
 * this is the one place the client-side read has to agree with it.
 */
export function isGranted(scope: Scope | undefined): boolean {
  return scope !== undefined && scope.kind !== "denied";
}

/**
 * Does `role` hold `capability` at all, in any scope? The UI's gate for offering a
 * control: it mirrors the matrix the routes enforce, so a button is only shown when
 * the request behind it can succeed. Scope ("own Account" vs "any") stays the server's
 * call — this answers only "is it ever allowed for this role".
 *
 * `role` is whatever the session carries. Anything `isNymblRole` doesn't recognise
 * (null, "", "user", a stale value) is denied — the matrix has no row for it.
 */
export function hasCapability(role: string | null | undefined, capability: Capability): boolean {
  if (!isNymblRole(role)) return false;
  return isGranted(PERMISSION_MATRIX[role][capability]);
}

/**
 * Does `role` hold `capability` with UNRESTRICTED ("any") scope — the whole book rather
 * than its own Accounts or Solutions? Reports → Availability covers every Resource's
 * hours, so it is offered (and served) only to roles that see everything.
 */
export function hasUnrestrictedCapability(
  role: string | null | undefined,
  capability: Capability,
): boolean {
  if (!isNymblRole(role)) return false;
  return PERMISSION_MATRIX[role][capability]?.kind === "any";
}
